import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Clock3, KeyRound, ShieldAlert, X } from 'lucide-react';
import { idemApi } from '../lib/api';

const statusStyles = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  denied: 'border-red-300 bg-red-50 text-red-700',
  expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  invalid: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const claimLabels = {
  identity_verified: 'Identity verified',
  full_name_verified: 'Full name verified',
  email_verified: 'Email verified',
  age_over_16: 'Age over 16',
  age_over_18: 'Age over 18',
  age_over_21: 'Age over 21',
  document_verified: 'Document verified',
  selfie_match_verified: 'Selfie match verified',
  identity_active: 'Identity active',
  trusted_device_available: 'Trusted device available',
  authentication_capable: 'Authentication capable',
  digital_key_account_active: 'Digital key account active',
  device_bound: 'Device bound',
  internal_assurance_policy: 'Internal assurance policy',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function timeRemaining(expiresAt) {
  if (!expiresAt) return '—';
  const milliseconds = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(milliseconds)) return '—';
  if (milliseconds <= 0) return 'Expired';
  const minutes = Math.ceil(milliseconds / 60000);
  return minutes === 1 ? 'Expires in 1 min' : `Expires in ${minutes} min`;
}

function statusLabel(status, expired) {
  if (expired) return 'expired';
  return String(status || 'invalid').replaceAll('_', ' ');
}

function RequestStatus({ status, expired = false }) {
  const normalized = expired ? 'expired' : status;
  return (
    <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[normalized] || statusStyles.invalid}`}>
      {statusLabel(normalized, false)}
    </span>
  );
}

function claimLabel(claimKey) {
  return claimLabels[claimKey] || claimKey;
}

export default function IdemRequest() {
  const { token } = useParams();
  const encodedToken = encodeURIComponent(token || '');
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [session, setSession] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [sessionError, setSessionError] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'idem request';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await idemApi.get(`/idem/requests/${encodedToken}`);
      setRequest(data.request || null);
    } catch (_error) {
      setRequest(null);
      setError('Request not found or the link is invalid.');
    } finally {
      setLoading(false);
    }
  }, [encodedToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await idemApi.get('/idem/session');
        if (!cancelled) {
          setSession(data.session || null);
          setSessionError('');
        }
      } catch (_error) {
        if (!cancelled) {
          setSession(null);
          setSessionError('Stored device session could not be verified.');
        }
      } finally {
        if (!cancelled) setSessionLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const expired = useMemo(() => request?.status === 'pending' && request.expires_at ? new Date(request.expires_at).getTime() <= Date.now() : false, [request]);
  const effectiveStatus = expired ? 'expired' : (request?.status || 'invalid');
  const sessionMismatch = Boolean(sessionLoaded && session && request && session.digital_key_account_id !== request.digital_key_account_id);
  const canRespond = Boolean(sessionLoaded && session && request && request.status === 'pending' && !expired && !sessionMismatch);

  const approve = async () => {
    if (!request) return;
    setWorking(true);
    setNotice('');
    setError('');
    try {
      const { data } = await idemApi.post(`/idem/requests/${encodedToken}/approve`);
      setRequest(data.request || null);
      setNotice('Request approved. The lab can now continue and finalize the safe claims preview.');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The request could not be approved.');
    } finally {
      setWorking(false);
    }
  };

  const deny = async (event) => {
    event.preventDefault();
    if (!request || !denyReason.trim()) return;
    setWorking(true);
    setNotice('');
    setError('');
    try {
      const { data } = await idemApi.post(`/idem/requests/${encodedToken}/deny`, { reason: denyReason.trim() });
      setRequest(data.request || null);
      setDenyReason('');
      setNotice('Request denied.');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The request could not be denied.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <header className="flex items-center gap-2.5 border-b border-zinc-200 pb-5">
          <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">idem</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Consumer request</span>
          </div>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-medium">Safe consumer authenticator link</p>
              <p className="mt-1 text-xs leading-5">This request is part of the internal idem lab flow. It does not create a production OIDC login, public OIDC provider, FAS/CSAM integration, official LoA, RRN/BIS processing, or expose raw identity evidence.</p>
            </div>
          </div>
        </section>

        {sessionLoaded && session && (
          <section className="mt-5 border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            <p className="font-medium">Paired lab authenticator active</p>
            <p className="mt-1 text-sky-800">Device <span className="font-mono">{session.device_public_id}</span> is paired to account <span className="font-mono">{session.digital_key_account_id}</span>.</p>
          </section>
        )}

        {sessionLoaded && !session && (
          <div className="mt-5 border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600">
            No paired device is active in this browser. Pair a lab authenticator before approving or denying this request.
          </div>
        )}

        {sessionError && (
          <div className="mt-5 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {sessionError}
          </div>
        )}

        {sessionMismatch && (
          <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
            This request does not belong to this authenticator.
          </div>
        )}

        {notice && <div className="mt-5 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}
        {error && <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Loading request…</div>
        ) : request ? (
          <>
            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Idem authenticator</p>
                  <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Approve request</h1>
                  <p className="mt-2 max-w-2xl text-sm text-zinc-600">Review the safe request details and approve or deny the login request.</p>
                </div>
                <RequestStatus status={effectiveStatus} expired={expired} />
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Request ID</p>
                  <p className="mt-1 break-all font-mono text-xs text-zinc-700">{request.id}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Relying party</p>
                  <p className="mt-1 text-sm text-zinc-700">{request.relying_party_name || request.request_context?.lab_client_id || 'idemgo-rp-simulator'}</p>
                  <p className="mt-1 text-xs text-zinc-500">{request.request_context?.purpose || 'digital_key_login_sandbox'}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Assurance</p>
                  <p className="mt-1 text-sm text-zinc-700">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiry</p>
                  <p className="mt-1 text-sm text-zinc-700">{formatDate(request.expires_at)}</p>
                  <p className="mt-1 text-xs text-zinc-500">{timeRemaining(request.expires_at)}</p>
                </article>
              </div>

              <div className="mt-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested claims</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(request.requested_claims || []).length ? request.requested_claims.map((claim) => (
                    <span key={claim} className="border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-[10px] text-zinc-600">
                      {claimLabel(claim)}
                    </span>
                  )) : (
                    <span className="text-sm text-zinc-500">No claims requested</span>
                  )}
                </div>
              </div>

              <div className="mt-5 border-t border-zinc-100 pt-4">
                <p className="text-sm font-medium text-zinc-800">Current state</p>
                <p className="mt-1 text-sm text-zinc-600">
                  {effectiveStatus === 'approved' && 'Approved. The lab can finalize this request.'}
                  {effectiveStatus === 'denied' && 'Denied. This request cannot continue.'}
                  {effectiveStatus === 'expired' && 'Expired. The request is no longer usable.'}
                  {effectiveStatus === 'cancelled' && 'Cancelled. The request is no longer usable.'}
                  {effectiveStatus === 'pending' && 'Pending. You can approve or deny this request.'}
                  {effectiveStatus === 'invalid' && 'This link is invalid.'}
                </p>
              </div>

              {canRespond ? (
                <div className="mt-5 flex flex-wrap items-start gap-3">
                  <button
                    type="button"
                    onClick={approve}
                    disabled={working}
                    className="inline-flex items-center gap-1.5 border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Approve
                  </button>
                  <form onSubmit={deny} className="flex-1 min-w-[280px]">
                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="idem-consumer-deny-reason">Deny reason</label>
                    <textarea
                      id="idem-consumer-deny-reason"
                      value={denyReason}
                      onChange={(event) => setDenyReason(event.target.value)}
                      required
                      maxLength={500}
                      rows={3}
                      className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"
                      placeholder="Add a short safe reason"
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        type="submit"
                        disabled={working || !denyReason.trim()}
                        className="inline-flex items-center gap-1.5 border border-red-200 px-3 py-2 text-sm text-red-700 hover:border-red-400 disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Deny
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="mt-5 border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
                  {effectiveStatus === 'approved' ? 'The request has been approved. Return to the lab flow to finalize.' : null}
                  {effectiveStatus === 'denied' ? 'The request has been denied.' : null}
                  {effectiveStatus === 'expired' ? 'The request has expired.' : null}
                  {effectiveStatus === 'cancelled' ? 'The request was cancelled.' : null}
                  {effectiveStatus === 'invalid' ? 'This request token is not valid.' : null}
                  {effectiveStatus === 'pending' && !session ? ' Pair a lab authenticator before you can respond to this request.' : null}
                </div>
              )}
            </section>

            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                <h2 className="font-display text-lg font-semibold">Safe request summary</h2>
              </div>
              <p className="mt-2 text-sm text-zinc-600">Only safe metadata is shown. This page never exposes raw evidence, documents, selfies, OCR/MRZ output, tokens, API keys, or secrets.</p>
            </section>
          </>
        ) : (
          <section className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">
            <p className="font-medium text-zinc-800">Request unavailable</p>
            <p className="mt-1">The request could not be loaded. The link may be invalid or the request may no longer exist.</p>
            <Link to="/" className="mt-4 inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-zinc-700 hover:border-zinc-400">
              <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
              Go home
            </Link>
          </section>
        )}
      </div>
    </main>
  );
}
